import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  CircularProgress, 
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { t } from '../utils/textUtils';

// Icons
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EngineeringIcon from '@mui/icons-material/Engineering';

const ProjectManagerDashboard = () => {
  const { user } = useAuth();
  const displayName = user?.fullName || user?.name || t('USER');
  // State
  const [loading, setLoading] = useState(true);
  const [activeProjects, setActiveProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [error, setError] = useState(null);

  // Fetch data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch projects
        const projectsResponse = await api.get('/projects');
        const projectsData = projectsResponse.data.projects || [];
        
        // Filter projects by status
        const active = projectsData.filter(p => 
          p.status === 'in_progress' || p.status === 'planning' || p.status === 'on_hold'
        );
        const completed = projectsData.filter(p => 
          p.status === 'completed' || p.status === 'closed'
        );
        
        setActiveProjects(active);
        setCompletedProjects(completed);
        
        // Fetch tasks due today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const tasksResponse = await api.get('/tasks', {
          params: {
            startDate: today.toISOString(),
            endDate: tomorrow.toISOString(),
            status: 'active'
          }
        });
        
        setTodayTasks(tasksResponse.data || []);
        
        // Fetch upcoming deadlines (next 7 days)
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const deadlinesResponse = await api.get('/tasks/deadlines', {
          params: {
            startDate: today.toISOString(),
            endDate: nextWeek.toISOString()
          }
        });
        
        // Sort deadlines by date (closest first)
        const sortedDeadlines = (deadlinesResponse.data || []).sort((a, b) => 
          new Date(a.dueDate) - new Date(b.dueDate)
        );
        
        setUpcomingDeadlines(sortedDeadlines);
        
        // Fetch recent documents
        const documentsResponse = await api.get('/documents/recent');
        setRecentDocuments(documentsResponse.data || []);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
        
        // Set dummy data for development/demo
        setDummyData();
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Set dummy data for development/demo
  const setDummyData = () => {
    const dummyProjects = [
      {
        id: 1,
        name: 'Proyek A',
        status: 'in_progress',
        progress: 65,
        target: 70,
        tasks: [
          { name: 'Pemasangan dinding', status: 'in_progress', daysRemaining: 2 },
          { name: 'Pengecoran lantai', status: 'pending', daysRemaining: 2 }
        ]
      },
      {
        id: 2,
        name: 'Proyek B',
        status: 'in_progress',
        progress: 50,
        target: 50,
        tasks: [
          { name: 'Struktur tanja', status: 'in_progress', daysRemaining: 0 },
          { name: 'Pekerjaan tanah', status: 'completed', daysRemaining: 0 }
        ]
      },
      {
        id: 3,
        name: 'Proyek C',
        status: 'delayed',
        progress: 40,
        target: 60,
        tasks: [
          { name: 'Plumbing', status: 'delayed', daysRemaining: -3 },
          { name: 'Pintasan in progress', status: 'in_progress', daysRemaining: 1 }
        ]
      },
      {
        id: 4,
        name: 'Proyek D',
        status: 'in_progress',
        progress: 80,
        target: 80,
        tasks: [
          { name: 'Pengecatan', status: 'in_progress', daysRemaining: 5 }
        ]
      }
    ];
    
    const dummyTasks = [
      { id: 1, name: 'Site inspection at Gudang', projectId: 1, projectName: 'Proyek A' },
      { id: 2, name: 'Follow up on vendor', projectId: 2, projectName: 'Proyek B' }
    ];
    
    const dummyDeadlines = [
      { 
        id: 1, 
        name: 'Progress Lantai 1 selesai', 
        projectId: 1, 
        projectName: 'Proyek A',
        dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
        progress: 75
      }
    ];
    
    const dummyDocuments = [
      { id: 1, name: 'Laporan mingguan', type: 'report', date: new Date() },
      { id: 2, name: 'Update gambar kerja', type: 'drawing', date: new Date() },
      { id: 3, name: 'RAB revisi', type: 'budget', date: new Date() },
      { id: 4, name: 'Berita acara', type: 'minutes', date: new Date() }
    ];
    
    setActiveProjects(dummyProjects.filter(p => p.status !== 'completed'));
    setCompletedProjects(dummyProjects.filter(p => p.status === 'completed'));
    setTodayTasks(dummyTasks);
    setUpcomingDeadlines(dummyDeadlines);
    setRecentDocuments(dummyDocuments);
    setLoading(false);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  // Get progress color
  const getProgressColor = (progress, target) => {
    const diff = progress - target;
    
    if (diff >= 0) return '#4CAF50'; // On track or ahead
    if (diff >= -5) return '#FF9800'; // Slightly behind
    return '#F44336'; // Significantly behind
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const primaryProject = activeProjects[0];
  const primaryDeadline = upcomingDeadlines[0];
  
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 1.5, sm: 2.5, md: 3 }, bgcolor: '#F8FAFC' }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: '#ffffff',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {t('PROJECT MANAGER')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('SELAMAT DATANG')}, {displayName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              component={Link}
              to="/projects"
              startIcon={<AccountTreeIcon />}
              sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#0b1324' } }}
            >
              {t('SEMUA PROYEK')}
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to="/tasks"
              startIcon={<AssignmentIcon />}
              sx={{ borderColor: '#0f172a', color: '#0f172a', '&:hover': { bgcolor: '#F8FAFC' } }}
            >
              {t('DAFTAR TUGAS')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))'
          },
          gap: 2,
          mb: 3
        }}
      >
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('PROYEK AKTIF')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{activeProjects.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('PROYEK SELESAI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{completedProjects.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('TUGAS HARI INI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{todayTasks.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
          <Typography variant="caption" color="text.secondary">{t('DEADLINE 7 HARI')}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{upcomingDeadlines.length}</Typography>
        </Paper>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>{t('FOKUS HARI INI')}</Typography>
              <Button size="small" component={Link} to="/tasks" endIcon={<ArrowForwardIosIcon fontSize="small" />} sx={{ color: '#0f172a', '&:hover': { bgcolor: '#F8FAFC' } }}>
                {t('LIHAT SEMUA')}
              </Button>
            </Box>
            <List sx={{ py: 0 }}>
              {todayTasks.length > 0 ? (
                todayTasks.map((task) => (
                  <ListItem key={task.id} sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssignmentIcon sx={{ color: '#F59E0B' }} />
                    </ListItemIcon>
                    <ListItemText primary={task.name} secondary={task.projectName || 'N/A'} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary={t('TIDAK ADA TUGAS UNTUK HARI INI')} primaryTypographyProps={{ color: 'text.secondary' }} />
                </ListItem>
              )}
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{t('DOKUMEN TERBARU')}</Typography>
            <List sx={{ py: 0 }}>
              {recentDocuments.length > 0 ? (
                recentDocuments.map((doc) => (
                  <ListItem key={doc.id} sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DescriptionIcon sx={{ color: '#2563EB' }} />
                    </ListItemIcon>
                    <ListItemText primary={doc.name} secondary={formatDate(doc.date)} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary={t('BELUM ADA DOKUMEN')} primaryTypographyProps={{ color: 'text.secondary' }} />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{t('PROYEK BERJALAN')}</Typography>
            {primaryProject ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{primaryProject.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('PROGRES')} {primaryProject.progress || 0}% / {primaryProject.target || 100}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={primaryProject.progress || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(15, 23, 42, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(primaryProject.progress, primaryProject.target || 100) }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button variant="outlined" size="small" component={Link} to={`/projects/${primaryProject.id}`} startIcon={<AccountTreeIcon />}>
                    {t('DETAIL')}
                  </Button>
                  <Button variant="contained" size="small" component={Link} to={`/progress/update?projectId=${primaryProject.id}`} startIcon={<EngineeringIcon />} sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#0b1324' } }}>
                    {t('UPDATE PROGRES')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('TIDAK ADA PROYEK AKTIF DITEMUKAN')}</Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{t('TENGGAT WAKTU MENDATANG')}</Typography>
            {primaryDeadline ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{primaryDeadline.name}</Typography>
                <Typography variant="body2" color="text.secondary">{formatDate(primaryDeadline.dueDate)}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={primaryDeadline.progress || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mt: 1,
                    bgcolor: 'rgba(15, 23, 42, 0.1)',
                    '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(primaryDeadline.progress || 0, 100) }
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('TIDAK ADA TENGGAT WAKTU MENDATANG')}</Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: 'divider', bgcolor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{t('PROYEK')}</Typography>
            {activeProjects.length > 0 ? (
              activeProjects.map((project) => (
                <Box key={project.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{project.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{project.status || t('BERJALAN')}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={project.progress || 0}
                    sx={{
                      height: 6,
                      borderRadius: 4,
                      mt: 1,
                      bgcolor: 'rgba(15, 23, 42, 0.1)',
                      '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(project.progress, project.target || 100) }
                    }}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">{t('TIDAK ADA PROYEK AKTIF DITEMUKAN')}</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectManagerDashboard;
